import { rigListQuerySchema, type RigKind, type TruckTrailerCoupling } from '@chukua/common';
import type { Dayjs } from 'dayjs';
import { useCallback, useState } from 'react';
import { useRemoteSearchOptions } from '../../../shared/hooks/use-remote-search-options';
import { useToast } from '../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';
import { coupleTrailer, listRigs } from '../features/units/units.api';
import { coupleOptions, type CoupleOption, type CoupleSubject } from '../policies/couple-options';

const PAGE_SIZE = 25;

function successMessage(subject: CoupleSubject, picked: CoupleOption, dropped: string | null): string {
  const [truck, trailer] = subject.kind === 'truck' ? [subject.truck.registration, picked.label] : [picked.label, subject.trailer.registration];
  return dropped ? `${trailer} coupled to ${truck}; ${dropped} dropped` : `${trailer} coupled to ${truck}`;
}

/**
 * The couple / swap drawer's state (gitaumoses4/chukua#390): the picker searches the Rigs list on the server and groups what it
 * finds, and a drop place is asked for only when a trailer comes off a truck here. Toasts once the server confirms. `subject`
 * must be stable (`useMemo`): a new one reloads the picker.
 */
export function useCoupleTrailer(subject: CoupleSubject, onCoupled: (coupling: TruckTrailerCoupling) => void) {
  const toast = useToast();
  const [pickedId, setPickedId] = useState<string | undefined>(undefined);
  const [dropZoneCode, setDropZoneCode] = useState<string | undefined>(undefined);
  const [coupledAt, setCoupledAt] = useState<Dayjs | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);

  const fetchOptions = useCallback(
    async (q: string | undefined) => {
      const kind: RigKind[] = subject.kind === 'truck' ? ['trailer_only', 'coupled'] : ['truck_only', 'coupled'];
      const page = await listRigs(rigListQuerySchema.parse({ q, kind, pageSize: PAGE_SIZE }));
      return coupleOptions(subject, page.items);
    },
    [subject],
  );
  const { selectProps } = useRemoteSearchOptions(fetchOptions);
  const picked = selectProps.options.find((option) => option.value === pickedId);
  /** The registration of the trailer this coupling takes off a truck here, if any. */
  const droppedTrailer = subject.kind === 'truck' ? subject.currentTrailerRegistration : (picked?.rig.trailer?.registration ?? null);
  const needsDropZone = droppedTrailer !== null;

  async function submit(): Promise<boolean> {
    if (!picked) return false;
    if (needsDropZone && !dropZoneCode) {
      setError('Choose where the trailer that comes off is dropped.');
      return false;
    }
    setSubmitting(true);
    setError(undefined);
    try {
      const [truckId, trailerId] = subject.kind === 'truck' ? [subject.truck.id, picked.value] : [picked.value, subject.trailer.id];
      const coupling = await coupleTrailer({ truckId, trailerId, coupledAt: coupledAt?.toISOString(), dropZoneCode: needsDropZone ? dropZoneCode : undefined });
      toast.success(successMessage(subject, picked, droppedTrailer));
      onCoupled(coupling);
      return true;
    } catch (err) {
      setError(apiErrorMessage(err) ?? "Couldn't couple the trailer. Try again.");
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  return { selectProps, picked, setPickedId, dropZoneCode, setDropZoneCode, coupledAt, setCoupledAt, needsDropZone, error, submitting, submit };
}
